import { useState, useMemo } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import { Badge } from '@/components/ui/badge';
import { Search, Filter, CalendarDays, Users, ClipboardList } from 'lucide-react';

const RICHIESTE = [
  { id: 1, paziente: 'Rossi Anna', eta: 76, tipo: 'Base', diagnosi: 'BPCO — monitoraggio domiciliare', stato: 'Attiva', scadenza: '15/06/2026', accessi: '2/sett', operatore: 'Inf. M. Bianchi' },
  { id: 2, paziente: 'Verdi Luca', eta: 68, tipo: 'Base', diagnosi: 'Diabete T2 — medicazioni piede', stato: 'Attiva', scadenza: '30/05/2026', accessi: '3/sett', operatore: 'Inf. R. Conti' },
  { id: 3, paziente: 'Neri Giuseppe', eta: 82, tipo: 'Complessa', diagnosi: 'Frattura femore — riabilitazione post-chirurgica', stato: 'Attiva', scadenza: '20/07/2026', accessi: '5/sett', operatore: 'Ft. E. Ferrara' },
  { id: 4, paziente: 'Ferrara Elena', eta: 72, tipo: 'Complessa', diagnosi: 'Neoplasia mammaria — cure palliative', stato: 'In Scadenza', scadenza: '25/04/2026', accessi: '4/sett', operatore: 'Inf. L. Moretti' },
  { id: 5, paziente: 'Esposito Luca', eta: 64, tipo: 'Base', diagnosi: 'IRC IV stadio — controllo parametri', stato: 'Attiva', scadenza: '10/08/2026', accessi: '2/sett', operatore: 'Inf. M. Bianchi' },
  { id: 6, paziente: 'Colombo Maria', eta: 88, tipo: 'Complessa', diagnosi: 'Demenza senile — supporto caregiver', stato: 'Nuova', scadenza: '—', accessi: 'Da definire', operatore: 'Da assegnare' },
  { id: 7, paziente: 'Romano Pietro', eta: 71, tipo: 'Base', diagnosi: 'Piede diabetico — medicazione avanzata', stato: 'Attiva', scadenza: '01/07/2026', accessi: '3/sett', operatore: 'Inf. R. Conti' },
];

const TIPO_COLORS = {
  Base: { bg: 'bg-blue-50 dark:bg-blue-950/30', text: 'text-blue-700 dark:text-blue-400' },
  Complessa: { bg: 'bg-violet-50 dark:bg-violet-950/30', text: 'text-violet-700 dark:text-violet-400' },
};

const STATO_COLORS = {
  Attiva: { bg: 'bg-emerald-50 dark:bg-emerald-950/30', text: 'text-emerald-700 dark:text-emerald-400', dot: 'bg-emerald-500' },
  'In Scadenza': { bg: 'bg-amber-50 dark:bg-amber-950/30', text: 'text-amber-700 dark:text-amber-400', dot: 'bg-amber-500' },
  Nuova: { bg: 'bg-sky-50 dark:bg-sky-950/30', text: 'text-sky-700 dark:text-sky-400', dot: 'bg-sky-500' },
};

const PILLS = ['Tutti', 'Base', 'Complessa'];

export default function ProgrammatoMMG() {
  const [search, setSearch] = useState('');
  const [filterTipo, setFilterTipo] = useState('Tutti');

  const filtered = useMemo(() => {
    return RICHIESTE.filter(r => {
      if (search) {
        const q = search.toLowerCase();
        if (!r.paziente.toLowerCase().includes(q) && !r.diagnosi.toLowerCase().includes(q)) return false;
      }
      if (filterTipo !== 'Tutti' && r.tipo !== filterTipo) return false;
      return true;
    });
  }, [search, filterTipo]);

  const countBase = RICHIESTE.filter(r => r.tipo === 'Base').length;
  const countComplessa = RICHIESTE.filter(r => r.tipo === 'Complessa').length;

  return (
    <AppLayout role="mmg">
      <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-5" data-testid="programmato-mmg-page">
        {/* Header */}
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-white" data-testid="programmato-title">Programmato MMG</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Panoramica delle richieste assistenziali in carico</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-950/30">
              <ClipboardList className="w-3.5 h-3.5 text-blue-500" />
              <span className="text-xs font-semibold text-blue-700 dark:text-blue-400">{countBase} Base</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-violet-50 dark:bg-violet-950/30">
              <ClipboardList className="w-3.5 h-3.5 text-violet-500" />
              <span className="text-xs font-semibold text-violet-700 dark:text-violet-400">{countComplessa} Complesse</span>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col gap-3" data-testid="toolbar">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Cerca per nome paziente o diagnosi..."
              className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm text-foreground placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-colors"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mr-1">Tipologia:</span>
            {PILLS.map(p => (
              <button key={p} onClick={() => setFilterTipo(p)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filterTipo === p ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 ring-1 ring-blue-300 dark:ring-blue-700' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
                data-testid={`pill-${p.toLowerCase()}`}
              >{p}</button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden" data-testid="programmato-table">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700/60">
                {['Paziente', 'Tipologia', 'Diagnosi / Motivo', 'Stato', 'Accessi', 'Scadenza', 'Operatore Rif.'].map(col => (
                  <th key={col} className="px-5 py-3 text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-slate-400">Nessuna richiesta trovata.</td></tr>
              ) : filtered.map(r => {
                const tc = TIPO_COLORS[r.tipo];
                const sc = STATO_COLORS[r.stato] || STATO_COLORS.Attiva;
                return (
                  <tr key={r.id} className="border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors" data-testid={`row-${r.id}`}>
                    <td className="px-5 py-3.5">
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{r.paziente}</p>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{r.eta} anni</p>
                    </td>
                    <td className="px-5 py-3.5">
                      <Badge className={`${tc.bg} ${tc.text} border-0 text-[10px] font-semibold px-2.5 py-0.5`}>{r.tipo}</Badge>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-xs text-slate-600 dark:text-slate-300">{r.diagnosi}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <Badge className={`${sc.bg} ${sc.text} border-0 text-[10px] font-semibold gap-1.5 px-2.5 py-0.5`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                        {r.stato}
                      </Badge>
                    </td>
                    <td className="px-5 py-3.5 text-xs text-slate-600 dark:text-slate-300">{r.accessi}</td>
                    <td className="px-5 py-3.5 text-xs text-slate-600 dark:text-slate-300">{r.scadenza}</td>
                    <td className="px-5 py-3.5 text-xs text-slate-600 dark:text-slate-300">{r.operatore}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </AppLayout>
  );
}
